/**
 * =============================================================================
 * TemplateTimeline — linha do tempo dos passos de uma execução
 *
 * Mostra cada módulo processado com bolinha colorida + palavra do estado,
 * seguindo o padrão visual usado na execução de fluxos.
 *
 * @version 1.0.0
 * RELEASE: 2026-09-08 v1.0.0 - Versão inicial (refatoração da tela).
 * =============================================================================
 */
import React from 'react';

import { cn } from '@/lib/utils';
import { TEMPLATE_MODULE_CATALOG } from '@/lib/template-system/moduleCatalog';
import type { TemplateModuleKey } from '@/lib/template-system/constants';

/** Cor da bolinha e palavra do estado de cada passo. */
const ESTADO: Record<string, { cor: string; label: string }> = {
  pending: { cor: 'bg-muted-foreground/40', label: 'Na fila' },
  running: { cor: 'bg-blue-500', label: 'Em andamento' },
  completed: { cor: 'bg-emerald-500', label: 'Concluído' },
  skipped: { cor: 'bg-amber-500', label: 'Ignorado' },
  failed: { cor: 'bg-destructive', label: 'Com erro' },
  compensated: { cor: 'bg-slate-500', label: 'Desfeito' },
};

export interface TemplateTimelineStep {
  module_key: string;
  step_index: number;
  status: string;
  retry_count: number;
  error_payload: Record<string, unknown> | null;
}

interface TemplateTimelineProps {
  steps: TemplateTimelineStep[];
}

export function TemplateTimeline({ steps }: TemplateTimelineProps) {
  if (steps.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        Nenhum passo registrado ainda para esta execução.
      </p>
    );
  }

  const ordenados = [...steps].sort((a, b) => a.step_index - b.step_index);

  return (
    <ol className="relative space-y-3 border-l pl-4">
      {ordenados.map((passo) => {
        const estado = ESTADO[passo.status] ?? { cor: 'bg-muted-foreground/40', label: passo.status };
        const nome =
          TEMPLATE_MODULE_CATALOG[passo.module_key as TemplateModuleKey]?.name ?? passo.module_key;
        const erro = passo.error_payload?.message as string | undefined;

        return (
          <li key={`${passo.module_key}-${passo.step_index}`} className="relative">
            <span
              className={cn(
                'absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full ring-2 ring-background',
                estado.cor,
              )}
            />
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm font-medium">{nome}</span>
              <span className="text-xs text-foreground">{estado.label}</span>
            </div>
            {passo.retry_count > 0 && (
              <p className="text-xs text-muted-foreground">
                Tentativas: {passo.retry_count + 1}
              </p>
            )}
            {erro && <p className="text-xs text-destructive">{erro}</p>}
          </li>
        );
      })}
    </ol>
  );
}

export default TemplateTimeline;
